import { memo } from 'react';
import { Link as RouterLink } from 'react-router';
import { alpha, Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { ArrowForward, Close } from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import { hasManagementRole } from '@/components/auth';
import { useLocalStorage } from '@/hooks';

export const GLOBAL_PROMPTS_BANNER_DISMISSED_KEY = 'global-prompts-banner-dismissed';

/**
 * Builds the per-user localStorage key for the banner's dismissed state. Namespacing by
 * user id keeps the dismissal "per user" on a shared browser while still surviving refresh
 * and re-login. Falls back to the base key when the user id is not yet available.
 */
export const getGlobalPromptsBannerKey = (userId: string | null): string =>
    userId ? `${GLOBAL_PROMPTS_BANNER_DISMISSED_KEY}:${userId}` : GLOBAL_PROMPTS_BANNER_DISMISSED_KEY;

/**
 * Promotional banner shown on the Projects page to surface the Global prompts feature.
 *
 * isible to admins and managers (the roles that can access Global prompts).
 * Dismissible; the dismissed state is persisted
 * per user via localStorage so it does not reappear after refresh or re-login.
 */
const GlobalPromptsBanner: React.FC = () => {
    const { role, userId } = useAuth();
    const [isDismissed, setIsDismissed] = useLocalStorage<boolean>(getGlobalPromptsBannerKey(userId), false);

    const canViewGlobalPrompts = hasManagementRole(role);

    if (!canViewGlobalPrompts || isDismissed) {
        return null;
    }

    return (
        <Paper
            elevation={0}
            sx={{
                mb: 1,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderRadius: '14px',
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.warning.main, 0.35),
                boxShadow: '0 1px 2px rgba(16, 22, 51, 0.04), 0 6px 20px rgba(16, 22, 51, 0.05)',
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
            }}
        >
            <Box
                aria-hidden="true"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    fontSize: 20,
                    lineHeight: 1,
                    borderRadius: '11px',
                    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.2),
                }}
            >
                💡
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Configure Global prompts</Typography>
                <Typography variant="body2" color="text.secondary">
                    Ask questions across all your projects and see the AI insights in one place.
                </Typography>
            </Box>

            <Button
                variant="contained"
                color="warning"
                component={RouterLink}
                to="/global-prompts"
                endIcon={<ArrowForward fontSize="small" />}
                sx={{ flexShrink: 0, textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}
            >
                Set them up
            </Button>

            <IconButton
                size="small"
                aria-label="Dismiss Global prompts banner"
                onClick={() => setIsDismissed(true)}
                sx={{ flexShrink: 0, color: 'text.secondary' }}
            >
                <Close fontSize="small" />
            </IconButton>
        </Paper>
    );
};

export default memo(GlobalPromptsBanner);
